import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import type { ScanResults, CategoryResult, RiskLevel } from '@wrm/shared';
import styles from './LimitedResultsView.module.css';

interface LimitedResultsViewProps {
  results: ScanResults;
  onScanAnother: () => void;
}

const riskLevelLabels: Record<RiskLevel, string> = {
  minimal: 'Low Priority',
  low: 'Monitor',
  moderate: 'Review Recommended',
  high: 'Attention Needed',
};

const CheckIcon = () => (
  <svg viewBox="0 0 20 20" fill="currentColor">
    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
  </svg>
);

const LockIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0110 0v4" />
  </svg>
);

export function LimitedResultsView({ results, onScanAnother }: LimitedResultsViewProps) {
  // Backend returns filtered results with hiddenCategories info
  const extendedResults = results as ScanResults & {
    hiddenCategories?: string[];
    isLimited?: boolean;
  };

  const hasHiddenContent = extendedResults.isLimited || extendedResults.hiddenCategories?.length;

  return (
    <section className={styles.results}>
      <div className={styles.container}>
        {/* Header */}
        <motion.div
          className={styles.header}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className={styles.headerBadge}>
            <CheckIcon />
            Scan Complete
          </div>
          <h2 className={styles.title}>Risk Assessment Results</h2>
          <p className={styles.url}>{results.url}</p>
        </motion.div>

        {/* Score Card */}
        <motion.div
          className={styles.scoreCard}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          <div className={styles.scoreWrapper}>
            <span className={styles.scoreValue}>{results.overallScore}</span>
            <span className={styles.scoreMax}>/100</span>
          </div>
          <p className={styles.scoreLabel}>Overall Risk Score</p>
          <span className={`risk-badge risk-${results.riskLevel}`}>
            {riskLevelLabels[results.riskLevel]}
          </span>
        </motion.div>

        {/* Categories Grid */}
        <div className={styles.categories}>
          {results.categories.map((category, index) => (
            <motion.div
              key={category.name}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 + index * 0.1 }}
            >
              <CategoryCard category={category} />
            </motion.div>
          ))}

          {/* Locked Category - Premium Upsell */}
          {hasHiddenContent && (
            <motion.div
              className={styles.lockedCard}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.5 }}
            >
              <div className={styles.lockedIcon}>
                <LockIcon />
              </div>
              <h3 className={styles.lockedTitle}>Compliance Assessment</h3>
              <p className={styles.lockedText}>
                GDPR, CCPA, accessibility compliance checks and detailed regulatory guidance included with a free account.
              </p>
              <Link to="/sign-up" className="btn btn-primary">
                Unlock Full Report
              </Link>
            </motion.div>
          )}
        </div>

        {/* Premium Features Banner */}
        {hasHiddenContent && (
          <motion.div
            className={styles.premiumBanner}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.6 }}
          >
            <div className={styles.premiumContent}>
              <h3>You're seeing 75% of your results</h3>
              <p>Create a free account to unlock the complete assessment and ongoing monitoring.</p>
              <div className={styles.premiumFeatures}>
                <span className={styles.premiumFeature}>
                  <CheckIcon /> All 4 categories
                </span>
                <span className={styles.premiumFeature}>
                  <CheckIcon /> Detailed findings
                </span>
                <span className={styles.premiumFeature}>
                  <CheckIcon /> Detailed reports
                </span>
                <span className={styles.premiumFeature}>
                  <CheckIcon /> Email alerts
                </span>
              </div>
            </div>
            <Link to="/sign-up" className="btn btn-primary btn-lg">
              Get Full Access Free
            </Link>
          </motion.div>
        )}

        {/* Actions */}
        <motion.div
          className={styles.actions}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.7 }}
        >
          <button onClick={onScanAnother} className="btn btn-secondary">
            Scan Another Website
          </button>
          <Link to="/sign-up" className="btn btn-primary">
            Create Free Account
          </Link>
        </motion.div>
      </div>
    </section>
  );
}

function CategoryCard({ category }: { category: CategoryResult }) {
  const topFindings = category.findings.slice(0, 4);
  const passCount = category.findings.filter(f => f.status === 'pass').length;
  const failCount = category.findings.filter(f => f.status === 'fail').length;

  return (
    <div className={styles.categoryCard}>
      <div className={styles.categoryHeader}>
        <h3 className={styles.categoryName}>{formatCategoryName(category.name)}</h3>
        <div className={styles.categoryScore}>
          <span className={styles.categoryScoreValue}>{category.score}</span>
          <span className={styles.categoryScoreMax}>/100</span>
        </div>
      </div>

      <div className={styles.categoryWeight}>
        {passCount} passed · {failCount} issues · {Math.round(category.weight * 100)}% weight
      </div>

      {topFindings.length > 0 && (
        <ul className={styles.findings}>
          {topFindings.map((finding) => (
            <li key={finding.id} className={styles.finding}>
              <span className={`${styles.findingStatus} ${styles[finding.status]}`}>
                {finding.status === 'pass' ? '✓' : finding.status === 'fail' ? '✗' : '?'}
              </span>
              <span className={styles.findingTitle}>{finding.title}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function formatCategoryName(name: string): string {
  return name.charAt(0).toUpperCase() + name.slice(1);
}
